<template>
  <div class="FinancesInvoicesExtraPaymentsTable finances-invoices-table">
    <PanelHeader
      title="Extra payments"
      :isOpen="show"
      :onToggleButtonClick="togglePanel"
      titleClassName="col-md-8"
      buttonsWrapperClassName="col-md-4"
    >
      <AddItemButton :onClick="addItem">{{addItemButtonLabel}}</AddItemButton>
    </PanelHeader>
    <b-collapse
      class="mx-3"
      style="position: relative;"
      v-model="show"
    >
      <EmptyTableMessage v-if="!items.length">
        There are no payments yet. Use "{{addItemButtonLabel}}" button.
      </EmptyTableMessage>
      <b-table v-else :fields="fields" :items="items" borderless>
        <template #cell(sales_commission_percent)="{ item, index }">
          <SalesCommissionSelect
            :value="item.sales_commission_percent"
            :options="salesCommissionOptions"
            :onChange="(val) => item.sales_commission_percent = val" />
        </template>
        <template #cell(cost)="{ item, index }">
          <CostInput
            v-model="item.cost"
            :currencyCode="currencyCode"
            @input="dataTouched(index, 'cost')"
          />
        </template>
        <template #cell(description)="{ item, index }">
          <b-form-textarea
            id="textarea"
            v-model="item.description"
            rows="2"
            max-rows="6"
            @input="dataTouched(index, 'description')"
          >
          </b-form-textarea>
        </template>
        <template #cell(actions)="data" v-if="items.length >= 1">
         <b-button
           variant="danger"
           size="sm w-100"
           @click.prevent="handleRemoveClick(data)"
         >
           <font-awesome-icon icon="trash" />
         </b-button>
        </template>
      </b-table>
    </b-collapse>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import PanelHeader from '@components/invoices/form/PanelHeader.vue'
import AddItemButton from '@components/modals/FinanceInvoicesForm/components/AddItemButton.vue'
import SalesCommissionSelect from '@components/modals/FinanceInvoicesForm/components/SalesCommissionSelect.vue'
import EmptyTableMessage from '@components/invoices/EmptyTableMessage.vue'
import CostInput from '@components/invoices/form/CostInput.vue'

const defaultItem = {
  'cost': 0,
  'description': '',
  'sales_commission_percent': 0
}

export default {
  components: { CostInput, EmptyTableMessage, SalesCommissionSelect, AddItemButton, PanelHeader },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    currencyCode: {
      type: String,
      default: ''
    },
    columnHeaderClass: {
      type: String,
      default: ''
    },
    invoiceType: {
      type: Object,
      default: () => null
    }
  },
  data () {
    return {
      show: false,
      addItemButtonLabel: 'Add payment',
      fields: [
        { key: 'cost', label: 'Cost', class: `cost-column ${this.columnHeaderClass}` },
        { key: 'sales_commission_percent', label: 'Sales commission', class: `sales-commission-column ${this.columnHeaderClass}` },
        { key: 'description', label: 'Description', class: this.columnHeaderClass },
        { key: 'actions', label: '', class: 'actions-column' }
      ],
      items: [],
      salesCommissionOptions: [0, 2.5, 5]
    }
  },
  mounted () {
    this.setInitialParams()
  },
  watch: {
    'value': {
      deep: true,
      handler () {
        this.setInitialParams()
      }
    }
  },
  methods: {
    setInitialParams () {
      this.items = this.value || []
      this.show = this.items.length > 0
      this.triggerInputEmit()
    },
    addItem () {
      this.items.push(cloneDeep(defaultItem))
      this.show = true
      this.triggerInputEmit()
    },
    handleRemoveClick (data) {
      // Check if this is "Only extra payments" type (ID 4) and it's the last item
      const isOnlyExtraPaymentsType = this.invoiceType && this.invoiceType.id === 4
      const isLastItem = this.items.length === 1

      if (isOnlyExtraPaymentsType && isLastItem) {
        // For "Only extra payments" type, clear values instead of removing the last item
        this.items[0].cost = 0
        this.items[0].description = ''
        this.items[0].sales_commission_percent = 0
        this.dataTouched(data.index, null)
        this.triggerInputEmit()
        return
      }

      // For other types or when there are multiple items, proceed with normal removal
      this.removeItem(data)
    },
    removeItem (data) {
      const removedIndex = data.index
      this.items = this.items.filter((item, index) => index !== removedIndex)
      this.show = this.items.length > 0
      this.dataTouched(data.index, null)
      this.triggerInputEmit()
      this.$emit('dataRemoved', { removedIndex })
    },
    triggerInputEmit () {
      this.$emit('input', this.items)
    },
    dataTouched (index, attrName) {
      this.$emit('dataTouched', { index, attrName })
    },
    togglePanel (event) {
      event.preventDefault()
      this.show = !this.show
    }
  }
}
</script>

<style lang="scss" scoped>
  .FinancesInvoicesExtraPaymentsTable {
    .currency-code {
      float: right;
      margin-top: -28px;
      margin-right: 4px;
    }
    .sales-commission-block {
      display: flex;
      align-items: center;
      margin: 5px -2px 5px 1px;
    }
    :global(.cost-column) {
      min-width: 110px !important;
    }
    :global(.sales-commission-column) {
      width: 110px;
    }
  }
</style>
